import { Link } from "react-router-dom";
import type { LandingPageContent } from "@shared/landingPages";
import { trackLandingEvent } from "../../lib/analytics";

/**
 * ManagedContentSections — the shared managed-content blocks rendered on both
 * conversion landing pages (/lp/dscsa-compliance and /software).
 *
 * Conversion sequence, each section answering a distinct question:
 *   1. Offer      — "What exactly do I get, and what's the catch?"
 *   2. Proof      — "What can I actually do once I start?"
 *   3. FAQ        — "What's still holding me back?" + final CTA
 */
export default function ManagedContentSections({ content }: { content: LandingPageContent }) {
  const trackCta = (location: string) => trackLandingEvent("landing_page_cta_click", window.location.pathname, { buttonLabel: content.ctaText, buttonLocation: location });
  return (
    <>
      {/* ── 1. Offer — value + eligibility + included checklist ─────── */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.15fr] gap-8 lg:gap-12 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-accent">Start with less risk</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-primary mt-2">{content.offer.name}</h2>
              <p className="text-gray-600 mt-3 leading-relaxed">{content.offer.eligibility}</p>
              <div className="flex flex-wrap gap-2 mt-5">
                {content.offer.reassurance.map((item) => <span key={item} className="rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800 px-3 py-1.5 text-sm font-semibold">{item}</span>)}
              </div>
              <Link to={content.ctaDestination} onClick={() => trackCta("offer-cta")} className="btn-primary inline-flex mt-6 px-6 py-3">{content.ctaText}</Link>
            </div>
            <div className="rounded-2xl bg-primary text-white p-7 lg:p-8 shadow-xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-300">Included when you start</p>
              <ul className="mt-5 space-y-4">
                {content.offer.included.map((item) => (
                  <li key={item} className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                      <svg className="w-3.5 h-3.5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-sm text-white/90 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-white/60 mt-6 leading-relaxed border-t border-white/10 pt-4">{content.offer.optionalNextSteps}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. Proof — concrete day-one outcomes ────────────────────── */}
      <section className="bg-background py-14 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-accent text-center">What you can do from day one</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-primary text-center mt-2">Built for confident next steps</h2>
          <ol className="mt-10 space-y-4">
            {content.proof.map((item, index) => (
              <li key={item} className="flex items-center gap-5 bg-white border-l-4 border-accent rounded-r-xl shadow-sm px-6 py-5">
                <span className="text-3xl font-extrabold text-accent/25 leading-none flex-shrink-0 tabular-nums" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                <p className="text-base text-gray-700 leading-relaxed">{item}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── 3. FAQ — objection handling + final CTA ─────────────────── */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-accent text-center">Common questions</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-primary text-center mt-2">Questions before you apply?</h2>
          <div className="mt-8 divide-y divide-gray-200 border-y border-gray-200">
            {content.faqs.map((faq) => <details key={faq.question} className="py-4 group"><summary className="cursor-pointer list-none flex justify-between gap-4 font-semibold text-primary rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2">{faq.question}<span className="text-accent group-open:rotate-45 transition-transform" aria-hidden="true">+</span></summary><p className="text-sm text-gray-600 leading-relaxed mt-3 pr-8">{faq.answer}</p></details>)}
          </div>
          <div className="text-center mt-8"><Link to={content.ctaDestination} onClick={() => trackCta("faq-cta")} className="btn-primary inline-flex px-7 py-3">{content.ctaText}</Link><p className="text-xs text-gray-500 mt-3">{content.reassurance}</p></div>
        </div>
      </section>
    </>
  );
}
